// src/pages/Dashboard/Admin/Articles/components/ArticleImageDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaImage, FaSave, FaTimes } from "react-icons/fa";

import { toAbsoluteArticlePhotoUrl } from "../../../../../services/articlesPhotoServices";

import { ARTICLE_FALLBACK_IMAGE } from "../utils/articlesDashboardUtils";

export default function ArticleImageDrawer({
  isOpen,
  article,
  uploading,
  onClose,
  onUpload,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [format, setFormat] = useState("webp");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFile(null);
    setPreviewUrl("");
    setProgress(0);
    setFormat("webp");
  }, [isOpen, article]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const currentImage = useMemo(() => {
    return toAbsoluteArticlePhotoUrl(article?.photo_url) || ARTICLE_FALLBACK_IMAGE;
  }, [article]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    await onUpload?.({
      file: selectedFile,
      format,
      onProgress: setProgress,
    });
  };

  if (!isOpen || !article) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Image article
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaImage className="text-indigo-600 dark:text-indigo-300" />
              Changer le visuel
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {article.nom || "Article sans nom"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  Image actuelle
                </p>

                <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                  <img
                    src={currentImage}
                    alt={article.nom || "Article"}
                    className="h-48 w-full object-contain"
                    onError={(event) => {
                      event.currentTarget.src = ARTICLE_FALLBACK_IMAGE;
                    }}
                  />
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  Nouvel aperçu
                </p>

                <div className="overflow-hidden rounded-[22px] border border-dashed border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-950">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Aperçu"
                      className="h-48 w-full object-contain"
                    />
                  ) : (
                    <div className="flex h-48 items-center justify-center px-4 text-center text-sm text-slate-500 dark:text-slate-400">
                      Sélectionnez une image pour afficher l’aperçu.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Fichier image
              </label>

              <input
                type="file"
                accept="image/*"
                disabled={uploading}
                onChange={(event) =>
                  setSelectedFile(event.target.files?.[0] || null)
                }
                className="block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition file:mr-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-bold file:text-indigo-700 hover:file:bg-indigo-100 focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:file:bg-indigo-500/10 dark:file:text-indigo-300"
              />

              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Formats recommandés : JPG, PNG ou WEBP. Taille maximale côté
                interface : 10 Mo.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Format de sortie
              </label>

              <select
                value={format}
                onChange={(event) => setFormat(event.target.value)}
                disabled={uploading}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              >
                <option value="webp">WEBP recommandé</option>
                <option value="jpg">JPG</option>
                <option value="png">PNG</option>
              </select>
            </div>

            {uploading ? (
              <div>
                <div className="mb-1 flex items-center justify-between text-xs font-bold text-slate-600 dark:text-slate-300">
                  <span>Transfert en cours</span>
                  <span>{progress}%</span>
                </div>

                <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#013E8E] to-[#4CA82F] transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={uploading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!selectedFile || uploading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {uploading ? "Envoi..." : "Sauvegarder l’image"}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}